import { defineStore } from 'pinia';
import { useDbStore } from '../dbStore';
import { useERezeptStore } from '../eRezeptStore';
import { useBasicResourcesStore } from './basicResources';

export const useMuster12ResourcesStore = defineStore('muster12Resources', {
  actions: {
    createCarePlanResource() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;

      const carePlanResource = {
        resourceType: 'CarePlan',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-careplan']
        },
        status: 'active',
        intent: 'order',
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        period: {
          start: formData.periodStart,
          end: formData.periodEnd
        },
        created: new Date().toISOString(),
        author: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        careTeam: [
          {
            reference: `CareTeam/${crypto.randomUUID()}`
          }
        ],
        addresses: [
          {
            reference: `Condition/${crypto.randomUUID()}`
          }
        ],
        activity: this.createActivityResources(),
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/einschraenkungen',
            valueString: formData.additionalNotes
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/erstverordnung',
            valueBoolean: formData.erstverordnung
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/folgeverordnung',
            valueBoolean: formData.folgeverordnung
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/unfall',
            valueBoolean: formData.unfall
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/ser',
            valueBoolean: formData.ser
          }
        ]
      };

      return carePlanResource;
    },
   
    createActivityResources() {
      const activities = [];
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;

      if (formData.medicationInput) {
        const medicationServiceRequest = this.createMedicationServiceRequest();
        eRezeptStore.setServiceRequest('medicationServiceRequest', medicationServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "18629005",
                  system: "http://snomed.info/sct",
                  display: "Medikamentengabe"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${medicationServiceRequest.id}`
          }
        });
      }

      if (formData.bloodSugarInitial || formData.bloodSugarIntensive) {
        const bloodSugarServiceRequest = this.createBloodSugarServiceRequest();
        eRezeptStore.setServiceRequest('bloodSugarServiceRequest', bloodSugarServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "33747003",
                  system: "http://snomed.info/sct",
                  display: "Blutzuckermessung"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${bloodSugarServiceRequest.id}`
          }
        });
      }

      if (formData.compressionStockingsPutOn || formData.compressionStockingsTakeOff || formData.compressionBandageApply || formData.compressionBandageRemove) {
        const compressionServiceRequest = this.createCompressionServiceRequest();
          eRezeptStore.setServiceRequest('compressionServiceRequest', compressionServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "225358003",
                  system: "http://snomed.info/sct",
                  display: "Kompressionsbehandlung"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${compressionServiceRequest.id}`
          }
        });
      }

      if (formData.woundType) {
        const woundCareServiceRequest = this.createWoundCareServiceRequest();
        eRezeptStore.setServiceRequest('woundCareServiceRequest', woundCareServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "385949008",
                  system: "http://snomed.info/sct",
                  display: "Wundversorgung"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${woundCareServiceRequest.id}`
          }
        });
      }

      if (formData.otherMeasures1 || formData.otherMeasures2) {
        const otherMeasuresServiceRequest = this.createOtherMeasuresServiceRequest();
        eRezeptStore.setServiceRequest('otherMeasuresServiceRequest', otherMeasuresServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "385763009",
                  system: "http://snomed.info/sct",
                  display: "Sonstige Maßnahmen der Behandlungspflege"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${otherMeasuresServiceRequest.id}`
          }
        });
      }

      if (formData.basicCareDetails || formData.householdCare) {
        const basicCareServiceRequest = this.createBasicCareServiceRequest();
        eRezeptStore.setServiceRequest('basicCareServiceRequest', basicCareServiceRequest);
        activities.push({
          detail: {
            kind: "ServiceRequest",
            status: "scheduled",
            code: {
              coding: [
                {
                  code: "78680009",
                  system: "http://snomed.info/sct",
                  display: "Grundpflege"
                }
              ]
            }
          },
          reference: {
            reference: `ServiceRequest/${basicCareServiceRequest.id}`
          }
        });
      }

      return activities;
    },

    createMedicationServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-medikamentengabe-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '18629005',
              display: 'Medikamentengabe'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.medicationAdministrationFrequency[0].frequency || 1,
            period: 1,
            periodUnit: 'd'
          }
        },
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/medikation-details',
            extension: [
              {
                url: 'medikament',
                valueCodeableConcept: {
                  coding: [
                    {
                      system: 'http://snomed.info/sct',
                      code: '372567009',
                      display: formData.medicationInput
                    }
                  ]
                }
              },
              {
                url: 'dosierung',
                valueDosage: {
                  timing: {
                    repeat: {
                      frequency: formData.medicationAdministrationFrequency[0].frequency || 1,
                      period: 1,
                      periodUnit: 'd'
                    }
                  }
                }
              },
              {
                url: 'verabreichungsweg',
                valueCodeableConcept: {
                  coding: [
                    {
                      system: 'http://snomed.info/sct',
                      code: '34206005',
                      display: 'Subcutaneous route'
                    }
                  ]
                }
              }
            ]
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/medikamentenbox-herrichtung',
            valueBoolean: formData.medicationPreparation
          }
        ]
      };
    },
    
    createBloodSugarServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-blutzuckermessung-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '33747003',
              display: 'Blutzuckermessung'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.bloodSugarFrequency[0].frequency || 1,
            period: 1,
            periodUnit: 'd'
          }
        },
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/erst-oder-neueinstellung',
            valueBoolean: formData.bloodSugarInitial
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/intensiviert-insulin',
            valueBoolean: formData.bloodSugarIntensive
          }
        ]
      };
    },
    
    createCompressionServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-kompressionsbehandlung-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '225358003',
              display: 'Kompressionsbehandlung'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.compressionStockingsFrequency[0].frequency || 1,
            period: 1,
            periodUnit: 'd'
          }
        },
        bodySite: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '69695003',
                display: 'Struktur des rechten Beins'
              }
            ]
          }
        ],
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/kompressionsart-details',
            extension: [
              {
                url: 'kompressionsstruempfe',
                valueCodeableConcept: {
                  coding: [
                    {
                      system: 'http://snomed.info/sct',
                      code: '229070002',
                      display: formData.compressionStockingsPutOn ? 'Anziehen von Kompressionsstrümpfen' : 'Ausziehen von Kompressionsstrümpfen'
                    }
                  ]
                }
              },
              {
                url: 'kompressionsverbaende',
                valueCodeableConcept: {
                  coding: [
                    {
                      system: 'http://snomed.info/sct',
                      code: '225369006',
                      display: formData.compressionBandageApply ? 'Anlegen von Kompressionsverband' : 'Abnehmen von Kompressionsverband'
                    }
                  ]
                }
              }
            ]
          }
        ]
      };
    },
    
    createWoundCareServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-wundversorgung-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '385949008',
              display: 'Wundversorgung'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.woundAcuteFrequency[0].frequency || 1,
            period: 1,
            periodUnit: 'd'
          }
        },
        bodySite: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '72001000',
                display: formData.woundLocation || 'Struktur der Fußsohle'
              }
            ]
          }
        ],
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/wundart-details',
            extension: [
              {
                url: 'wundart',
                valueString: formData.woundType
              },
              {
                url: 'aktuelleGroesse',
                valueString: formData.woundSize
              },
              {
                url: 'aktuellerGrad',
                valueString: formData.woundGrade
              },
              {
                url: 'praeparate',
                valueString: formData.woundMaterials
              },
              {
                url: 'versorgungsart',
                valueCodeableConcept: {
                  coding: [
                    {
                      system: 'http://snomed.info/sct',
                      code: formData.woundAcute ? '225359006' : '225360001',
                      display: formData.woundAcute ? 'Akute Wundversorgung' : 'Chronische Wundversorgung'
                    }
                  ]
                }
              }
            ]
          }
        ]
      };
    },
    
    createOtherMeasuresServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-sonstige-massnahmen-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '385763009',
              display: 'Sonstige Maßnahmen der Behandlungspflege'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.otherMeasures1Frequency[0].frequency || 1,
            period: 1,
            periodUnit: 'd'
          }
        },
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/anleitung-details',
            extension: [
              {
                url: 'anleitungsArt',
                valueString: formData.patientInstruction
              },
              {
                url: 'anzahl',
                valuePositiveInt: parseInt(formData.patientInstructionCount)
              }
            ]
          }
        ]
      };
    },
    
    createBasicCareServiceRequest() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const formData = eRezeptStore.muster12FormData;
      return {
        resourceType: 'ServiceRequest',
        id: crypto.randomUUID(),
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/odil-grundpflege-hauswirtschaft-service-request']
        },
        status: 'active',
        intent: 'order',
        category: [
          {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '385763009',
                display: 'Behandlungspflege'
              }
            ]
          }
        ],
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '78680009',
              display: 'Grundpflege'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        },
        requester: {
          reference: `Practitioner/${dbStore.getSelectedPractitioner.uuid}`
        },
        performer: [
          {
            reference: `Organization/${formData.organizationId}`
          }
        ],
        occurrenceTiming: {
          repeat: {
            boundsPeriod: {
              start: formData.periodStart,
              end: formData.periodEnd
            },
            frequency: formData.basicCareFrequency[0].frequency,
            period: 1,
            periodUnit: 'd'
          }
        },
        extension: [
          {
            url: 'http://fhir.odilab.de/StructureDefinition/unterstuetzungspflege',
            valueBoolean: formData.basicCare
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/krankenhausvermeidungspflege',
            valueBoolean: formData.preventiveCare
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/grundpflege-details',
            valueBoolean: formData.basicCareDetails
          },
          {
            url: 'http://fhir.odilab.de/StructureDefinition/hauswirtschaftliche-versorgung-details',
            valueBoolean: formData.householdCare
          }
        ]
      };
    },

    createMuster12Bundle() {
      const dbStore = useDbStore();
      const eRezeptStore = useERezeptStore();
      const basicResourcesStore = useBasicResourcesStore();

      const bundle = {
        resourceType: 'Bundle',
        id: crypto.randomUUID(),
        meta: {
          profile: [
            'http://fhir.odilab.de/StructureDefinition/ODIL-Bundle'
          ]
        },
        identifier: {
          system: 'https://gematik.de/fhir/erp/NamingSystem/GEM_ERP_NS_PrescriptionId',
          value: dbStore.createTaskResponse.prescriptionId
        },
        type: 'document',
        timestamp: new Date().toISOString(),
        entry: []
      };

      // Composition
      const compositionResource = this.createCompositionResource({
        id: crypto.randomUUID(),
        patientId: dbStore.getSelectedPatient.easytiUUID,
        practitionerId: dbStore.getSelectedPractitioner.uuid,
        organizationId: dbStore.getSelectedOrganization.uuid,
        carePlanId: 'BeispielCarePlan',
        coverageId: dbStore.getSelectedPatient.insuranceInformations.uuid,
        musterType: 'e12H',
        date: new Date().toISOString(),
        legalBasis: '00',
      });
      bundle.entry.push({ fullUrl: `Composition/${compositionResource.id}`, resource: compositionResource });

      // CarePlan
      const carePlanResource = this.createCarePlanResource();
      bundle.entry.push({ fullUrl: `CarePlan/${carePlanResource.id}`, resource: carePlanResource });

      // ServiceRequests
      const serviceRequests = eRezeptStore.serviceRequests;
      Object.values(serviceRequests).forEach(serviceRequest => {
        if (serviceRequest) {
          bundle.entry.push({ fullUrl: `ServiceRequest/${serviceRequest.id}`, resource: serviceRequest });
        }
      });

      // Patient
      const patientResource = basicResourcesStore.createPatientResource(dbStore.getSelectedPatient);
      bundle.entry.push({ fullUrl: `Patient/${patientResource.id}`, resource: patientResource });

      // Practitioner
      const practitionerResource = basicResourcesStore.createPractitionerResource(dbStore.getSelectedPractitioner);
      bundle.entry.push({ fullUrl: `Practitioner/${practitionerResource.id}`, resource: practitionerResource });

      // Organization
      const organizationResource = basicResourcesStore.createOrganizationResource(dbStore.getSelectedOrganization);
      bundle.entry.push({ fullUrl: `Organization/${organizationResource.id}`, resource: organizationResource });

      // Coverage
      const coverageResource = basicResourcesStore.createCoverageResource(dbStore.getSelectedPatient);
      bundle.entry.push({ fullUrl: `Coverage/${coverageResource.id}`, resource: coverageResource });

      // CareTeam
      const careTeamResource = this.createCareTeamResource();
      bundle.entry.push({ fullUrl: `CareTeam/${careTeamResource.id}`, resource: careTeamResource });

      // Condition
      const conditionResource = this.createConditionResource(dbStore);
      bundle.entry.push({ fullUrl: `Condition/${conditionResource.id}`, resource: conditionResource });

      return bundle;
    },

    createCompositionResource(compositionData) {
      const compositionResource = {
        resourceType: 'Composition',
        id: compositionData.id,
        meta: {
          profile: ['http://fhir.odilab.de/StructureDefinition/Odil-CompositionHiMi']
        },
        extension: [
          {
            url: 'https://fhir.kbv.de/StructureDefinition/KBV_EX_FOR_Legal_basis',
            valueCoding: {
              system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_SFHIR_KBV_STATUSKENNZEICHEN',
              code: compositionData.legalBasis
            }
          }
        ],
        status: 'final',
        type: {
          coding: [
            {
              system: 'https://fhir.odilab.de/ValueSet/VS_HIMI_HEIMI_KBV_FORMULAR_ART',
              code: compositionData.musterType
            }
          ]
        },
        subject: {
          reference: `Patient/${compositionData.patientId}`
        },
        date: compositionData.date,
        author: [
          {
            reference: `Practitioner/${compositionData.practitionerId}`,
            type: 'Practitioner'
          },
          {
            type: 'Device',
            identifier: {
              system: 'https://fhir.kbv.de/NamingSystem/KBV_NS_FOR_Pruefnummer',
              value: 'Y/400/2107/36/999'
            }
          }
        ],
        title: 'elektronische Pflegeverordnung',
        custodian: {
          reference: `Organization/${compositionData.organizationId}`
        },
        section: [
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Prescription'
                }
              ]
            },
            entry: [
              {
                reference: `CarePlan/${compositionData.carePlanId}`
              }
            ]
          },
          {
            code: {
              coding: [
                {
                  system: 'https://fhir.kbv.de/CodeSystem/KBV_CS_ERP_Section_Type',
                  code: 'Coverage'
                }
              ]
            },
            entry: [
              {
                reference: `Coverage/${compositionData.coverageId}`
              }
            ]
          }
        ]
      };

      return compositionResource;
    },


    createCareTeamResource() {
      return {
        resourceType: 'CareTeam',
        id: crypto.randomUUID(),
        status: 'active',
        name: 'Pflegeteam'
      };
    },

    createConditionResource(dbStore) {
      return {
        resourceType: 'Condition',
        id: crypto.randomUUID(),
        code: {
          coding: [
            {
              system: 'http://snomed.info/sct',
              code: '404684003',
              display: 'Klinischer Befund'
            }
          ]
        },
        subject: {
          reference: `Patient/${dbStore.getSelectedPatient.easytiUUID}`
        }
      };
    }
  }
});